import React, { useState, useEffect } from "react";
import Cookies from "universal-cookie";
import { logo } from "../../assets/svg/svg.js";

import { useSelector, useDispatch } from 'react-redux';
import { getHostHelperContract } from '../../helper/contract-helper';
import { toast, Bounce } from 'react-toastify';
import { transactionAction } from '../../helper/chain-helper';
import { getBlockedList } from '../../helper/settingsHelper.js';
import Loader from "../../components/Loader.js";
import { InputTags } from 'react-bootstrap-tagsinput';
import 'react-bootstrap-tagsinput/dist/index.css';
import { CompleteLoader } from "../../modals/common-alert.js";
import { reportError, toErrorString } from "../../utils/errorLogger.js";
import { DatePicker, Tooltip } from 'antd';

const cookies = new Cookies();
const userName = localStorage.getItem('UserName');
const BlockedAddressesSettings = () => {
    const [loader, setLoader] = useState(false);
    const [user] = useState(cookies.get("userObject"));
    const [account, setAccount] = useState('');
    const [sectionLoader, setSectionLoader] = useState(true);
    const [blockedAddresses, setBlockedAddresses] = useState([]);
    const dispatch = useDispatch();
    const refreshSettings = useSelector((state) => state.refreshSettings);

    useEffect(() => {
        const init = async () => {
            if (window.ncogProvider) {
                try {
                    const walletAddress = localStorage.getItem("walletAddress");
                    setAccount(walletAddress);
                } catch (error) {
                    const errorStr = toErrorString(error || error.message);
                    reportError({
                        endpoint: '-',
                        message: `FileName: FilterEmails.js, Fun: init, ${String(error.message || 'Unhandled error')}`,
                        error: errorStr,
                        userName: userName || '',
                        level: 'warn',
                    });
                    console.error("Error fetching ncog accounts:", error);
                    toast.error("Could not connect to wallet.", { position: "top-center" });
                }
            } else {
                console.warn("ncogProvider not found.");
                toast.warn("Wallet provider not found. Please ensure it's available.", { position: "top-center" });
            }
        };
        init();
    }, []);

    useEffect(() => {
        if (user?.name) {
            fetchBlockedAddressesData();
        }
    }, [user, refreshSettings]);

    const fetchBlockedAddressesData = async () => {
        if (!user?.name) {
            console.warn("User not available for fetching blocked list.");
            setSectionLoader(false);
            return;
        }
        setSectionLoader(true);
        try {
            const getBlocked = await getBlockedList();
            const addresses = getBlocked.address ? getBlocked.address.split(',').filter(addr => addr.trim() !== '') : [];
            setBlockedAddresses(addresses.map(addr => addr.toLowerCase()));
        } catch (error) {
            const errorStr = toErrorString(error || error.message);
            reportError({
                endpoint: '-',
                message: `FileName: FilterEmails.js, Fun: fetchBlockedAddressesData, ${String(error.message || 'Unhandled error')}`,
                error: errorStr,
                userName: userName || '',
                level: 'warn',
            });
            console.error("Error fetching blocked addresses:", error);
            toast.error("Failed to load blocked addresses.", { position: "top-center" });
            setBlockedAddresses([]);
        } finally {
            setSectionLoader(false);
        }
    };

    const handleTagsChange = (value) => {
        // Ensure value.values is an array
        const newTags = Array.isArray(value.values) ? value.values : [];
        const uniqueTags = new Set();
        const validTags = [];

        newTags.forEach(tag => {
            const trimmedTag = tag.trim().toLowerCase();
            if (trimmedTag !== '' && !uniqueTags.has(trimmedTag)) {
                if (/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]/.test(trimmedTag)) {
                    uniqueTags.add(trimmedTag);
                    validTags.push(trimmedTag);
                } else {
                    toast.warn(`"${tag}" is not a valid address format.`, { position: "top-center" });
                }
            }
        });
        setBlockedAddresses(validTags);
    };

    const handleUnblock = (addressToUnblock) => {
        const normalizedAddressToUnblock = addressToUnblock.toLowerCase();
        const updatedAddresses = blockedAddresses.filter(addr => addr.toLowerCase() !== normalizedAddressToUnblock);
        setBlockedAddresses(updatedAddresses);
    };

    const saveBlockedAddressesConfiguration = async () => {
        if (!account) {
            toast.error("Wallet account not connected.", { position: "top-center" });
            return;
        }
        if (!user?.name) {
            toast.error("User information not available.", { position: "top-center" });
            return;
        }

        setLoader(true);
        const options = [
            {
                key: "address",
                value: blockedAddresses.join(',')
            },
        ];
        const helperContract = getHostHelperContract();
        const functionParams = [user.name, 'blockedAddress', options];

        try {
            const hash = await transactionAction(helperContract, "updateSettings", functionParams, account, 'helper');
            if (hash) {
                dispatch({ type: "REFRESH_SETTINGS", payload: !refreshSettings });
                toast.success("Blocked addresses updated successfully.", {
                    position: "top-center",
                    transition: Bounce,
                });
            } else {
                toast.error("Failed to update settings. Transaction may have failed.", {
                    position: "top-center",
                    transition: Bounce,
                });
            }
        } catch (error) {
            const errorStr = toErrorString(error || error.message);
            reportError({
                endpoint: '-',
                message: `FileName: FilterEmails.js, Fun: saveBlockedAddressesConfiguration, ${String(error.message || 'Unhandled error')}`,
                error: errorStr,
                userName: userName || '',
            });
            console.error("Error saving blocked addresses:", error);
            toast.error(`Error saving settings: ${error.message || "Something went wrong."}`, {
                position: "top-center",
                transition: Bounce,
            });
        } finally {
            setLoader(false);
        }
    };

    return (
        <>
            <div className="d-flex flex-column flex-md-row align-items-start py-3 gap-3">
                <h3 className="setting-labels max-width-210">
                    The following addresses are blocked. Messages from these addresses will not appear in inbox:
                </h3>
                <div className="flex-grow-1 w-100">
                    {/* Input section for adding new blocked addresses */}
                    <div className="mb-3">
                        <div className="d-flex align-items-start gap-1" >
                            <label htmlFor="blocked-address-input" className="form-label fw-medium">
                                Enter addresses to block:
                            </label>
                            <Tooltip title="Type each email and separate them with Enter or commas." placement="top" >
                                {logo.info_circle_svg}
                            </Tooltip>
                        </div>
                        <InputTags
                            values={blockedAddresses}
                            onTags={handleTagsChange}
                            className="form-control"
                            id="blocked-address-input"
                            placeholder="Type address and press Enter"
                            tagProps={{
                                className: 'badge bg-secondary me-1 mb-1 block-input',
                                classNameRemove: 'bi bi-x-circle-fill ms-1',
                            }}
                            inputProps={{
                                className: 'form-control', // Ensure input field styling
                            }}
                        />
                        <div className="form-text filter-email-text-small-dark" >
                            Separate addresses with a comma or by pressing Enter.
                        </div>
                    </div>
                    {/* Display current blocked addresses */}
                    {sectionLoader ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <Loader normal={true} />
                        </div>
                    ) : blockedAddresses.length === 0 ? (
                        <div className="p-3 text-center text-muted blockedAddresses-dashed-border" >
                            No addresses are currently blocked.
                        </div>
                    ) : (
                        <div className="scrollable-max-height">
                            {blockedAddresses.map((address, index) => {
                                const username = address.split('@')[0] || "User";
                                return (
                                    <div key={`${address}-${index}`} className={`d-flex align-items-center justify-content-between gap-3 px-2 py-2 address-item ${index === blockedAddresses.length - 1 ? 'last' : ''}`}>
                                        <div className="d-flex align-items-center gap-2">
                                            <img className="rounded-circle object-fit-cover square-30" src="/no_profile.png" alt="" />
                                            <div>
                                                <h4 className="setting-username mb-0">{username}</h4>
                                                <h5 className="setting-user-email filter-email-text-small-dark" >{address}</h5>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleUnblock(address)}
                                            className="cursor-pointer red-button-text"
                                            aria-label={`Unblock ${address}`}
                                        >
                                            Unblock
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
            {/* Save Button */}
            <div className="mt-4 d-flex justify-content-end save-settings-btn">
                <button
                    onClick={saveBlockedAddressesConfiguration}
                    className="save-settings-btn"
                    disabled={loader || sectionLoader}
                >
                    {loader ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                        </>
                    ) : (
                        <span>Save Changes</span>
                    )}
                </button>
            </div>
            <CompleteLoader isOpen={loader && !sectionLoader} />
        </>
    );
};

export default BlockedAddressesSettings;